import { FunctionComponent, useContext, useState } from 'react'
import styled from '@emotion/styled'
import { LocalizedSlugsContext } from '../../../../hooks/useLocalizedSlugs'
import { NavigationCta } from '../types'
import { Button } from '../../../atoms/button'
import { Text } from '../../../atoms/text'
import { AvailableLocales } from '../../../../utils/locale'
import { useBreakpoints } from '../../../../hooks/useBreakpoints'
import { useLocale } from '../../../../hooks/useLocale'
import { Link } from 'gatsby'
import { trimSlashes } from '../../../../utils/links'

const NavigationAction: FunctionComponent<NavigationCta> = ({ link, title, openInNewTab }) => {
  const { isTabletOrLess } = useBreakpoints()

  const { locale } = useLocale()
  const { localizedSlugs } = useContext(LocalizedSlugsContext)

  const getTargetLocalePath = (targetLocale: AvailableLocales): string => {
    if (!localizedSlugs.slugs) return `/${targetLocale}`

    const matchingSlug = localizedSlugs.slugs.find(slug => slug.locale === targetLocale)
    const normalizedSlug = trimSlashes(matchingSlug?.slug ?? '')
    const targetPath = matchingSlug ? `/${targetLocale}/${normalizedSlug}` : `/${targetLocale}`
    return targetPath
  }

  return (
    <CtaWrapper>
      <LanguageCtaWrapper role="radiogroup" aria-label="Language selection">
        <LanguageButton
          as="a"
          href={getTargetLocalePath('en')}
          variant={isTabletOrLess ? 'outlined' : 'ghost'}
          theme="background_is_darker"
          role="radio"
          aria-label="Switch to English"
          aria-checked={locale === 'en'}
          tabIndex={locale === 'fr' ? 0 : -1}
        >
          <ButtonText fontStyle="label-m" as="span">
            EN
          </ButtonText>
        </LanguageButton>
        <LanguageButton
          as="a"
          href={getTargetLocalePath('fr')}
          variant={isTabletOrLess ? 'outlined' : 'ghost'}
          theme="background_is_lighter"
          role="radio"
          aria-label="Switch to French"
          aria-checked={locale === 'fr'}
          tabIndex={locale === 'en' ? 0 : -1}
        >
          <ButtonText fontStyle={'label-m'} as="span">
            FR
          </ButtonText>
        </LanguageButton>
      </LanguageCtaWrapper>
      <CtaButton
        as={openInNewTab ? 'a' : Link}
        {...(openInNewTab ? { href: `${link}` } : { to: `${link}` })}
        target={openInNewTab ? '_blank' : '_self'}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        trailingIcon="right-arrow"
        animation={true}
      >
        <Text fontStyle={isTabletOrLess ? "label-m" : "label-s"}>{title}</Text>
      </CtaButton>
    </CtaWrapper>
  )
}

const CtaWrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 1279px) {
    margin-inline: 1rem;
  }

  @media (min-width: 1280px) {
    flex-direction: row;
    gap: var(--gap-l);
  }
`

const LanguageCtaWrapper = styled.div`
  display: flex;
  flex-direction: row;

  gap: var(--gap-m);

  @media (max-width: 1279px) {
    margin-block: 1.5rem;
  }
`

const LanguageButton = styled(Button)`
  & > p {
    font-weight: var(--medium);
  }

  &[aria-checked='false'] {
    color: var(--neutral-550);
  }

  @media (max-width: 1279px) {
    width: auto;
    border-color: var(--neutral-550);
  }
  @media (min-width: 1280px) {
    height: 100%;
    padding-inline: 0;

    position: relative;

    &[aria-checked='true']::after {
      content: '';

      position: absolute;
      left: 0;
      bottom: calc(50% - 0.75rem);

      height: 1px;
      width: 100%;

      background-color: var(--neutral-950);
    }
  }
`

const ButtonText = styled(Text)`
  font-weight: var(--medium);
`

const CtaButton = styled(Button)`
  white-space: nowrap;
  @media (min-width: 1280px) {
    width: 10rem;
  }
`

export default NavigationAction
